<template>
  <v-braintree
    :authorization="authKey"
    :vaultManager="true"
    :three-d-secure="true"
    :three-d-secure-parameters="getCardPayConfig"
    :paypal="getPaypalPayConfig"
    :applePay="getApplePayConfig"
    :translations="translations"
    @load="onLoad"
    @success="onSuccess"
  >
    <template #button="slotProps">
      <VBtn
        :class="{'disabled': isPayButtonDisabled }"
        class="w-100 shadow-none"
        color="brandingPrimary"
        data-test="payButton"
        dark
        @click="slotProps.submit"
        @click.stop="$emit('trackGtmEventPayButtonClick')"
      >
        <span class="button">
          {{ $t('checkout.paymentForm.pay') }}
        </span>
      </VBtn>
    </template>
  </v-braintree>
</template>

<script lang="ts">
export default {
  name: 'PaymentForm',
};
</script>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n, useVm } from '../../../composables';

const props = defineProps({
  amount: {
    type: Number | String,
    required: true,
  },
  authKey: {
    type: String,
    required: true,
  },
  googleMerchantId: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['onPaymentSuccess', 'trackGtmEventPayButtonClick']);

const { t } = useI18n();
const vm = useVm();

const instance = ref(null);
const isPayButtonDisabled = ref(true);
const translations = {
  chooseAWayToPay: t('checkout.paymentForm.chooseAWayToPay'),
  chooseAnotherWayToPay: t('checkout.paymentForm.chooseAnotherWayToPay'),
  payWithCard: t('checkout.paymentForm.payWithCard'),
  cardNumberLabel: t('checkout.paymentForm.cardNumberLabel'),
  expirationDateLabel: t('checkout.paymentForm.expirationDateLabel'),
  expirationDateLabelSubheading: t('checkout.paymentForm.expirationDateLabelSubheading'),
  cvvLabel: t('checkout.paymentForm.cvvLabel'),
  fieldInvalidForCvv: t('checkout.paymentForm.fieldInvalidForCvv'),
  fieldInvalidForExpirationDate: t('checkout.paymentForm.fieldInvalidForExpirationDate'),
  fieldInvalidForNumber: t('checkout.paymentForm.fieldInvalidForNumber'),
  fieldEmptyForNumber: t('checkout.paymentForm.fieldEmptyForNumber'),
  cvvThreeDigitLabelSubheading: t('checkout.paymentForm.cvvThreeDigitLabelSubheading'),
};

const getCardPayConfig = computed(() => ({
  amount: props.amount,
}));

const getPaypalPayConfig = computed(() => ({
  flow: 'checkout',
  amount: props.amount,
  currency: 'EUR',
}));

const getApplePayConfig = computed(() => ({
  displayName: 'My Store',
  paymentRequest: {
    total: {
      label: 'My Store',
      amount: props.amount,
    },
    // requiredBillingContactFields: ['postalAddress'],
  },
}));

const getGooglePayConfig = computed(() => ({
  googlePayVersion: 2,
  merchantId: props.googleMerchantId,
  transactionInfo: {
    totalPriceStatus: 'FINAL',
    totalPrice: `${props.amount}`,
    currencyCode: 'EUR',
  },
  allowedPaymentMethods: [{
    type: 'CARD',
    parameters: {
      billingAddressRequired: true,
      billingAddressParameters: {
        format: 'FULL',
      },
    },
  }],
}));

function onLoad(loadedInstance) {
  instance.value = loadedInstance;

  loadedInstance.on('paymentMethodRequestable', () => {
    isPayButtonDisabled.value = false;
  });

  loadedInstance.on('noPaymentMethodRequestable', () => {
    isPayButtonDisabled.value = true;
  });

  loadedInstance.on('paymentOptionSelected', () => {
    vm.isButtonDisabled = true;
  });
}

async function onSuccess(payload) {
  emit('onPaymentSuccess', payload);
}
</script>
